// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import { CLDLoopPolarity } from './drawing/cld/CLDLoopLabel';

/**
 * Pure core for the Loop Picker (CO-4) and the CLD loop badges. Both surfaces
 * must agree on which loop is "R1", so the polarity+ordinal numbering lives here
 * as a single source of truth rather than being recomputed per surface.
 */

/** A structural feedback loop as the engine reports it (no behavior data). */
export interface StructuralLoop {
  id: string;
  polarity: CLDLoopPolarity;
  variables: readonly string[];
}

/** A loop prepared for display in the ranked picker. */
export interface RankedLoop {
  id: string;
  /** Stable badge label ('R1', 'B2', '?3') tied to loop identity. */
  label: string;
  polarity: CLDLoopPolarity;
  variables: readonly string[];
  /** Relative LTM (Loops That Matter) importance, when an LTM run supplied it. */
  score?: number;
}

/**
 * Count the connected components the loops form -- two loops are in the same
 * component when they share a variable (transitively). More than one means the
 * model contains disconnected loop systems that a single-canvas render would
 * otherwise show as if they were one diagram; the Loops view warns on this
 * (Williams 2026-08-07). Union-find over the loops' member variables; returns 0
 * for no loops. Pure, so it unit-tests without a model.
 */
export function loopComponentCount(loops: readonly StructuralLoop[]): number {
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    let root = x;
    while (parent.get(root) !== root) {
      root = parent.get(root) as string;
    }
    // Path-compress so repeated finds stay cheap.
    let cur = x;
    while (parent.get(cur) !== root) {
      const next = parent.get(cur) as string;
      parent.set(cur, root);
      cur = next;
    }
    return root;
  };
  const union = (a: string, b: string): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) {
      parent.set(ra, rb);
    }
  };

  for (const loop of loops) {
    for (const v of loop.variables) {
      if (!parent.has(v)) {
        parent.set(v, v);
      }
    }
    // Union every member to the first, so a loop is one connected unit and loops
    // sharing any variable collapse into the same component.
    const [first, ...rest] = loop.variables;
    if (first !== undefined) {
      for (const v of rest) {
        union(first, v);
      }
    }
  }

  const roots = new Set<string>();
  for (const key of parent.keys()) {
    roots.add(find(key));
  }
  return roots.size;
}

/**
 * Map each loop id to its badge label, numbering reinforcing / balancing /
 * unknown loops independently in the order the engine reports them (R1, R2, B1,
 * ...). The label is a function of loop identity + position, never of layout or
 * sort, so the canvas badge and the picker row for a loop always match.
 */
export function loopLabels(loops: readonly StructuralLoop[]): Map<string, string> {
  const labels = new Map<string, string>();
  let reinforcing = 0;
  let balancing = 0;
  let unknown = 0;
  for (const loop of loops) {
    let label: string;
    if (loop.polarity === 'reinforcing') {
      reinforcing += 1;
      label = `R${reinforcing}`;
    } else if (loop.polarity === 'balancing') {
      balancing += 1;
      label = `B${balancing}`;
    } else {
      unknown += 1;
      label = `?${unknown}`;
    }
    labels.set(loop.id, label);
  }
  return labels;
}

/**
 * The ranked list of loops for the picker (CO-4). The engine already returns
 * loops sorted by average importance, so that input order IS the ranking before
 * any explicit LTM run -- the fallback preserves it rather than imposing a
 * structural order that would fight the engine's sort. When per-loop LTM scores
 * are supplied (a WS3 analyze run over the current overrides), loops re-sort by
 * descending score so the ranking tracks the parameters the user just played
 * with; unscored loops sink to the bottom keeping their relative order.
 *
 * Labels are assigned from the engine order first, so a loop keeps the same
 * label regardless of how the rows are sorted.
 */
export function rankLoops(
  loops: readonly StructuralLoop[],
  scores?: ReadonlyMap<string, number>,
): RankedLoop[] {
  const labels = loopLabels(loops);
  const ranked: RankedLoop[] = loops.map((loop) => ({
    id: loop.id,
    label: labels.get(loop.id) ?? loop.id,
    polarity: loop.polarity,
    variables: loop.variables,
    score: scores?.get(loop.id),
  }));

  if (ranked.some((r) => r.score !== undefined)) {
    // Highest LTM importance first; unscored loops sink to the bottom but keep a
    // stable relative order (Array.prototype.sort is stable).
    ranked.sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity));
  }
  // No scores: leave the engine's importance order untouched.
  return ranked;
}
